import { useState, useEffect, useRef, useCallback } from "react";
import { Spin, Button, Collapse } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/hooks/useAuth";
import { ReloadTokenService } from "@/services";
import type { TokenEventData, SSESubscription } from "@/services";
import { APIError } from "@/lib/api-client";

/**
 * QR Code Content Component
 * Client Component that handles QR code loading and SSE event subscription
 * Separated from page shell to enable Suspense boundaries
 *
 * **Auth Architecture:**
 * - Uses ONLY `useAuth()` for server auth status (single source of truth)
 * - LINE login is automatic - handled by LineProvider (transparent prerequisite)
 * - All API calls use server userId from useAuth()
 *
 * **SSE Implementation:**
 * - Uses Server-Sent Events (SSE) via @microsoft/fetch-event-source
 * - Supports Authorization headers (Bearer token) for secure authentication
 * - No token exposure in URLs/logs (security best practice)
 * - Cleaner than long polling with lower server overhead
 */
export default function QRCodeContent() {
  const { isLoggedIn, isAuthenticating, userId } = useAuth();
  const [token, setToken] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{
    type: "404" | "other";
    message: string;
  } | null>(null);
  const [eventResult, setEventResult] = useState<TokenEventData | null>(null);
  const subscriptionRef = useRef<SSESubscription | null>(null);

  // Load token function (extracted for retry capability)
  const loadToken = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const tokenValue = await ReloadTokenService.getMyToken(userId);
      setToken(tokenValue);
      setError(null);
      setLoading(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "無法載入 QR Code";
      const is404 = error instanceof APIError && error.statusCode === 404;
      setError({
        type: is404 ? "404" : "other",
        message: errorMessage,
      });
      setLoading(false);
    }
  }, [userId]);

  // Load token on mount (only when authenticated and userId is available)
  useEffect(() => {
    if (isAuthenticating || !isLoggedIn || !userId) return;
    loadToken();
  }, [isAuthenticating, isLoggedIn, userId, loadToken]);

  // Subscribe to SSE events when token is loaded
  useEffect(() => {
    if (!token || !userId) return;

    // Subscribe to token events with callbacks
    const subscription = ReloadTokenService.subscribeToTokenEvents(
      userId,
      token,
      {
        onMessage: (data) => {
          // Token processed successfully
          setEventResult(data);
          // Close the connection after receiving the event
          subscription.abort();
        },
        onError: (error) => {
          console.error("SSE connection error:", error);
          // Connection will be closed automatically on error
        },
        onOpen: () => {
          console.log("SSE connection established");
        },
      }
    );

    subscriptionRef.current = subscription;

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.abort();
        subscriptionRef.current = null;
      }
    };
  }, [token, userId]);

  if (isAuthenticating || !isLoggedIn || !userId) {
    return null;
  }

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: eventResult ? "flex-start" : "center",
        backgroundColor: "white",
        padding: "32px 24px",
        paddingTop: eventResult ? "40px" : "32px",
        overflow: "auto",
      }}
    >
      {error ? (
        // Show error/empty state with action-focused design
        <div
          style={{
            width: "100%",
            maxWidth: "400px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {error.type === "404" ? (
            // Empty state - calm and informative (not an error)
            <>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                  textAlign: "center",
                  marginBottom: "12px",
                  lineHeight: "1.2",
                }}
              >
                目前沒有可用的回收憑證
              </div>

              <div
                style={{
                  fontSize: "15px",
                  color: "#8C8C8C",
                  textAlign: "center",
                  lineHeight: "1.6",
                  maxWidth: "280px",
                }}
              >
                取得權限後，回收憑證會自動出現在這裡
              </div>
            </>
          ) : (
            // Error state - action-first hierarchy
            <>
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "12px",
                  lineHeight: 1,
                }}
              >
                😕
              </div>

              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                  textAlign: "center",
                  marginBottom: "20px",
                  lineHeight: "1.2",
                }}
              >
                載入失敗
              </div>

              <Button
                type="primary"
                size="large"
                onClick={loadToken}
                loading={loading}
                style={{
                  width: "100%",
                  maxWidth: "320px",
                  height: "52px",
                  borderRadius: "8px",
                  backgroundColor: "#FFCD14",
                  borderColor: "#FFCD14",
                  fontSize: "18px",
                  fontWeight: 500,
                  marginBottom: "16px",
                }}
              >
                {loading ? "載入中..." : "再試一次"}
              </Button>

              <div
                style={{
                  fontSize: "15px",
                  color: "#8C8C8C",
                  textAlign: "center",
                  lineHeight: "1.5",
                  marginBottom: "24px",
                  maxWidth: "300px",
                }}
              >
                試試看重新整理，還是不行的話可以跟我們說
              </div>

              {/* Technical details - collapsible for debugging */}
              <Collapse
                ghost
                size="small"
                style={{ width: "100%", maxWidth: "340px" }}
                items={[
                  {
                    key: "1",
                    label: (
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#999",
                        }}
                      >
                        查看技術詳情
                      </span>
                    ),
                    children: (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          lineHeight: "1.6",
                          wordBreak: "break-word",
                          fontFamily: "monospace",
                          backgroundColor: "#F5F5F5",
                          padding: "12px",
                          borderRadius: "4px",
                          marginTop: "8px",
                        }}
                      >
                        {error.message}
                      </div>
                    ),
                  },
                ]}
              />
            </>
          )}
        </div>
      ) : eventResult ? (
        // Show result after successful SSE event
        <div
          style={{
            width: "100%",
            maxWidth: "400px",
            padding: "20px",
          }}
        >
          {/* Success Message */}
          <div
            style={{
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 600,
              color: "#40B9D8",
              marginBottom: "32px",
            }}
          >
            ✓ 成功登記回收
          </div>

          {/* Information Card */}
          <div
            style={{
              backgroundColor: "#f8f8f8",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            {/* Store Name */}
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "8px",
                }}
              >
                回收店家
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  color: "#1D1D1B",
                }}
              >
                {eventResult.store_name || "未知的店舖"}
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "8px",
                }}
              >
                手機號碼
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  color: "#1D1D1B",
                }}
              >
                {eventResult.phone}
              </div>
            </div>
          </div>
        </div>
      ) : loading ? (
        // Show spinner while loading token
        <>
          <div
            style={{
              marginBottom: "20px",
              fontSize: "16px",
              color: "#1D1D1B",
              textAlign: "center",
            }}
          >
            請將此 QR Code 放至機器掃描區
          </div>
          <div
            style={{
              padding: "20px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              width: "296px",
              height: "296px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin size="large" />
          </div>
        </>
      ) : (
        // Show QR Code while polling
        <>
          <div
            style={{
              marginBottom: "20px",
              fontSize: "16px",
              color: "#1D1D1B",
              textAlign: "center",
            }}
          >
            請將此 QR Code 放至機器掃描區
          </div>
          <div
            style={{
              padding: "20px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <QRCodeSVG value={token} size={256} level="H" />
          </div>
          <div
            style={{
              marginTop: "20px",
              fontSize: "14px",
              color: "#4A4A4A",
              textAlign: "center",
            }}
          >
            等待掃描中...
          </div>
        </>
      )}
    </div>
  );
}
